// Load polyfills (once, on the top of our web app)
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { createRoot } from 'react-dom/client';

import { authClient } from '@40seas/auth-client';
import { isReevolHostname, getResolvedUrls } from '@40seas/frontend/libs/configs/consts';
import { ModalContext, SetupModalContext } from '@40seas/frontend/libs/contexts';
import { SetupModal, SetupStripe } from '@40seas/frontend/libs/entities';
import { GoCardlessDropin } from '@gocardless/react-dropin';

import { Plaid } from 'react-plaid-link';

import { IntentsDataProvider, useIntentsAuthProvider } from '@40seas/payments-shared';
import type { Stripe } from '@stripe/stripe-js';

import { SetupAuth0Service, SetupStripeService, SetupUrlsService } from '@40seas/frontend/libs/controllers/services';
import { BrandConfigProvider, FeatureToggleProvider } from '@40seas/frontend/libs/providers';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from '../auth/AuthProvider';
import { stripeLoadingProvider } from '../providers/stripeLoadingProvider';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

export class RenderService {
    renderApp = async (App: JSX.Element, config: { isAuthenticationEnabled: boolean } = { isAuthenticationEnabled: true }) => {
        const setup = getSetupIntent();

        // Configure authClient with hostname-resolved auth-v2 URL (Reevol vs 40Seas)
        const resolvedAuthV2Url = isReevolHostname() ? getResolvedUrls().AUTH_V2_URL : process.env.AUTH_V2_URL;
        if (resolvedAuthV2Url) {
            authClient.configure({ baseUrl: resolvedAuthV2Url });
        }

        (window as any).__SETUP__ = setup;

        const urlParams = new URLSearchParams(window.location.search);
        const sameOriginFT = !!urlParams.get('same-origin');

        const setupUrlsService = new SetupUrlsService(sameOriginFT);

        if (config.isAuthenticationEnabled) {
            const setupAuth0Service = new SetupAuth0Service(setupUrlsService);
            setup.auth0 = await setupAuth0Service.get({ apiUrl: setup.urls.API_URL });
        }

        const setupStripeService = new SetupStripeService();

        window.stripePromise = async ({ tenantId, token, useConnectedAccount = true }) => {
            if (!window.stripeSetup) {
                window.stripeSetup = await setupStripeService.get(setup.urls.API_URL, setup.environment, {
                    ...(tenantId ? { '40seas-tenant': tenantId } : {}),
                    ...(token ? { '40seas-token': token } : {}),
                });
            }

            return stripeLoadingProvider(useConnectedAccount);
        };

        const isMobile = window.innerWidth <= 1000;

        const authProvider = useIntentsAuthProvider();

        const dataProvider = new IntentsDataProvider(setup.intentType, setup.urls.API_URL, authProvider);

        const context = { ...setup, isMobile, dataProvider, isAuthenticationEnabled: config.isAuthenticationEnabled };

        const rootNode = document.getElementById('root')!;
        const root = createRoot(rootNode);

        root.render(
            <FeatureToggleProvider setup={setup}>
                <BrandConfigProvider>
                    <AuthProvider isAuthenticationEnabled={config.isAuthenticationEnabled}>
                        <QueryClientProvider client={queryClient}>
                            <SetupModalContext.Provider value={setup}>
                                <ModalContext.Provider value={context}>{App}</ModalContext.Provider>
                            </SetupModalContext.Provider>
                        </QueryClientProvider>
                    </AuthProvider>
                </BrandConfigProvider>
            </FeatureToggleProvider>,
        );
    };
}

const getSetupIntent = (): SetupModal => {
    const urlParams = new URLSearchParams(window.location.search);

    const jwtToken = urlParams.get('t');

    const getParsedJwt = () => {
        try {
            return (jwtToken && JSON.parse(atob(jwtToken.split('.')[1]))) || {};
        } catch (e) {
            console.error(`Failed to parse JWT token: ${jwtToken}`, e);
            return {};
        }
    };

    const jwt = getParsedJwt();

    const resolvedUrls = isReevolHostname() ? getResolvedUrls() : undefined;

    const setup = {
        intentType: jwt.intentType,
        environment: jwt.environment,
        urls: {
            API_URL: resolvedUrls?.API_PATH ?? (process.env.API_URL as string),
            APP_URL: resolvedUrls?.APP_PATH ?? process.env.APP_URL,
            DASHBOARD_URL: resolvedUrls?.DASHBOARD_PATH ?? process.env.DASHBOARD_URL,
            REEVOL_DASHBOARD_URL: process.env.REEVOL_DASHBOARD_URL,
        } as any,
        auth0: {
            domain: process.env.AUTH0_DOMAIN as string,
            clientId: process.env.AUTH0_CLIENT_ID as string,
        },
        HOSTNAME: process.env.HOSTNAME,
    };

    if (!!urlParams.get('same-origin')) {
        setup.urls.API_URL = 'https://app-test.40seas.com/_api';
    }

    return setup;
};

declare global {
    interface Window {
        stripePromise: (options: {
            tenantId?: string;
            token?: string;
            useConnectedAccount?: boolean;
        }) => Promise<void | Stripe | null> | null;
        stripeSetup: SetupStripe;
        GoCardlessDropin: GoCardlessDropin;
        Plaid: Plaid;
    }
}
